import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { combineLatest, Observable, of } from 'rxjs';
import { LoaderComponent } from '@sukna/shared';
import { AppInitializationService } from './core/services/app-initialization.service';
import { AppService } from './core/services/app.service';
import { VersionCheckService } from './core/services/version-check.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    imports: [RouterOutlet, AsyncPipe, LoaderComponent],
})
export class AppComponent implements OnInit {
    private readonly versionCheckService = inject(VersionCheckService);
    private readonly translate = inject(TranslateService);
    readonly initializing$: Observable<boolean>;
    readonly translationsReady$: Observable<boolean>;
    private destroyRef = inject(DestroyRef);

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private titleService: Title,
        private appInitService: AppInitializationService,
        private appService: AppService,
    ) {
        this.initializing$ = combineLatest([
            this.appInitService.initializing$,
            this.appService.translationsLoading$,
        ]).pipe(map(([appInitializing, translationsLoading]) => appInitializing || translationsLoading));
        this.translationsReady$ = this.appService.translationsLoading$.pipe(
            map((loading) => !loading),
        );
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map(() => this.activatedRoute),
                map((route) => {
                    while (route.firstChild) route = route.firstChild;
                    return route;
                }),
                filter((route) => route.outlet === 'primary'),
                switchMap((route) =>
                    route.data.pipe(
                        map((routeData: any) => routeData['title'] ?? ''),
                        switchMap((titleKey) => {
                            if (!titleKey) {
                                return of('IMS - Sukna capital');
                            }
                            return this.translate.stream(titleKey).pipe(
                                map((translated) => {
                                    const text =
                                        typeof translated === 'string'
                                            ? translated
                                            : translated &&
                                                typeof translated === 'object' &&
                                                typeof (translated as { title?: unknown }).title ===
                                                    'string'
                                              ? String((translated as { title: string }).title)
                                              : titleKey;
                                    return `${text} | IMS - Sukna capital`;
                                }),
                            );
                        }),
                    ),
                ),
                distinctUntilChanged(),
                tap((title) => this.titleService.setTitle(title)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    ngOnInit(): void {
        // Deploy soft-updates (enabled only in deployed dev/qa/staging/prod environments).
        this.versionCheckService.start();
    }
}
